import React, { createContext, useContext, useMemo, useReducer, useState } from 'react';
import { produce } from 'immer';
import {
  EmergentExemplar,
  ExemplarWord,
  EmergentContentSection,
  WordsAndSentencesContent,
  WordsPageContent,
  IWordsAndSentencesImageParams,
  IWordsImageParams,
  IDefaultImages,
  AWSImage,
  ImageDimensions,
  ImageSide,
  ReplacedImage,
} from '../types';
import { WORDS } from '../constants/exemplar';
import { convertSavedLinesToLines, getHiddenImagesFromSavedContent } from '../utils/editTool';
import { StyledWordContext } from './styledWordContext';
import { DrawingContext } from './drawingContext';
import { findEmergentContentByPage } from '../utils';
import { Type, UndoRedoAction } from '../hooks/useUndoRedo';
import { UndoRedoContext } from './undoRedoContext';
import { isEmpty } from 'lodash';

export enum ActionTypes {
  SET_EXEMPLAR = 'SET_EXEMPLAR',
  SET_FONT_STYLE = 'SET_FONT_STYLE',
  UPDATE_RELATIONSHIPS = 'UPDATE_RELATIONSHIPS',
  REPLACE_IMAGE = 'REPLACE_IMAGE',
  SET_HIGHLIGHTED = 'SET_HIGHLIGHTED',
}

const initialState: EmergentExemplar = {} as EmergentExemplar;

type payload = ExemplarWord | EmergentExemplar | IWordsImageParams | IWordsAndSentencesImageParams | IDefaultImages;

export interface IAction {
  type: string;
  payload: payload;
}

type Dispatch = {
  exemplarState: typeof initialState;
  dispatch: (action: IAction) => void;
  setExemplar: (exemplar: EmergentExemplar, isForce?: boolean) => void;
  handleFontStyle: (word: ExemplarWord) => void;
  updateRelationships: (defaultImages: AWSImage[]) => void;
  replaceImage: (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions,
    sectionId: string,
    side?: ImageSide
  ) => void;
  setSavedEdits: (exemplar: EmergentExemplar, page: string) => void;
  saveEmergentImageUpdates: (prevState: ReplacedImage, state: ReplacedImage) => void;
  saveTextUpdates: (prevState: EmergentExemplar, state: EmergentExemplar) => void;
  handleWordHighlight: (word: ExemplarWord) => void;
  saveHighlightsUpdates: (prevState: ExemplarWord, state: ExemplarWord) => void;
};

const getProduce = (state: EmergentExemplar, action: IAction) => {
  return produce(state, draft => {
    const word = action.payload as ExemplarWord;
    if (word?.type) {
      const section = draft.content.find(todo => todo?.label === word.label) as WordsAndSentencesContent;
      let side = section.content.left as EmergentContentSection[];
      if (word.side === 'right') {
        side = section.content.right as EmergentContentSection[];
      }
      side[word.sectionIndex].lines[word.lineIndex].words[word.wordIndex] = word;
    }
  });
};

const replaceWordsAndSentencesImage = (item: WordsPageContent, action: IAction) => {
  return {
    ...(item as WordsAndSentencesContent),
    content: {
      ...(item as WordsAndSentencesContent).content,
      [(action.payload as IWordsAndSentencesImageParams).side]: [
        ...(item as WordsAndSentencesContent).content[(action.payload as IWordsAndSentencesImageParams).side].map(
          item => {
            if (item.type === 'image') {
              if (item.uuid === (action.payload as IWordsAndSentencesImageParams).imageUUIDtoReplace) {
                if (!item.default_uuid) {
                  return {
                    ...item,
                    uuid: (action.payload as IWordsAndSentencesImageParams).uuid,
                    default_uuid: item.uuid,
                  };
                }
                return {
                  ...item,
                  uuid: (action.payload as IWordsAndSentencesImageParams).uuid,
                };
              }
              return item;
            }
            return item;
          }
        ),
      ],
    },
  };
};

const replaceImage = (state: EmergentExemplar, action: IAction) => {
  return {
    ...(state as EmergentExemplar),
    content: [
      ...state.content.map(item => {
        if (item.label !== WORDS) {
          return replaceWordsAndSentencesImage(item, action);
        }
        return item;
      }),
    ],
  };
};

const exemplarReducer = (state: EmergentExemplar, action: IAction): EmergentExemplar => {
  switch (action.type) {
    case ActionTypes.SET_EXEMPLAR: {
      return {
        ...(action.payload as EmergentExemplar),
      };
    }
    case ActionTypes.SET_FONT_STYLE:
      return getProduce(state, action);
    case ActionTypes.UPDATE_RELATIONSHIPS: {
      return {
        ...state,
        relationships: {
          ...state.relationships,
          images: [...state.relationships.images, ...(action.payload as IDefaultImages).defaultImages],
        },
      };
    }
    case ActionTypes.SET_HIGHLIGHTED:
      return getProduce(state, action);
    case ActionTypes.REPLACE_IMAGE:
      return replaceImage(state, action);
    default:
      return state;
  }
};

export const ExemplarContext = createContext<Dispatch>({
  exemplarState: initialState,
  dispatch: () => false,
  setExemplar: () => false,
  handleFontStyle: () => false,
  updateRelationships: () => false,
  replaceImage: () => false,
  setSavedEdits: () => null,
  saveEmergentImageUpdates: () => null,
  saveTextUpdates: () => null,
  handleWordHighlight: () => false,
  saveHighlightsUpdates: () => null,
});

export const ExemplarProvider: React.FC = ({ children }): JSX.Element => {
  const [state, dispatch] = useReducer(exemplarReducer, initialState);
  const { handleHideText, setImages, handleHideHighlightsTool } = useContext(StyledWordContext);
  const { lines, setLines, setDrawingsHidden } = useContext(DrawingContext);
  const [isSavedExemplarSet, setSavedExemplar] = useState(false);
  const { registerUndoRedoCreator } = useContext(UndoRedoContext);

  const setExemplar = (exemplar: EmergentExemplar, isForce = false) => {
    const payload = {
      ...exemplar,
    };

    if (isForce || isEmpty(state)) {
      dispatch({ type: ActionTypes.SET_EXEMPLAR, payload: payload });
    }
  };

  const handleFontStyle = (word: ExemplarWord) => {
    dispatch({ type: ActionTypes.SET_FONT_STYLE, payload: word });
  };

  const handleUpdateRelationships = (defaultImages: AWSImage[]) => {
    dispatch({ type: ActionTypes.UPDATE_RELATIONSHIPS, payload: { defaultImages } });
  };

  const handleReplaceImage = (
    imageUUIDtoReplace: string,
    uuid: string,
    default_uuid: string | undefined,
    defaultImageDimensions: ImageDimensions,
    selectedImageDimensions: ImageDimensions,
    sectionId: string,
    side?: ImageSide
  ) => {
    dispatch({
      type: ActionTypes.REPLACE_IMAGE,
      payload: {
        imageUUIDtoReplace,
        uuid,
        default_uuid,
        defaultImageDimensions,
        selectedImageDimensions,
        sectionId,
        side,
      },
    });
  };

  const setSavedEdits = (exemplar: EmergentExemplar, page: string) => {
    const { tools } = exemplar;

    if (tools && !isSavedExemplarSet) {
      setSavedExemplar(true);
      const pageContent = findEmergentContentByPage(exemplar.content, page);

      if (pageContent && 'content' in pageContent) {
        setImages(getHiddenImagesFromSavedContent(pageContent.content));
      }

      const { pen, isTextHidden, highlights } = tools;

      handleHideText(!!isTextHidden);

      if (pen) {
        const { lines: savedLines, isHidden } = pen;
        setLines({ ...lines, [page]: convertSavedLinesToLines(savedLines) });
        setDrawingsHidden(isHidden);
      }

      if (highlights?.isHidden) {
        handleHideHighlightsTool();
      }
    }
  };

  const saveImageUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.replaceImage, (payload: UndoRedoAction) => {
        const {
          imageUUIDtoReplace,
          uuid,
          default_uuid,
          sectionId,
          side,
          defaultImageDimensions,
          selectedImageDimensions,
        } = payload as ReplacedImage;
        handleReplaceImage(
          imageUUIDtoReplace,
          uuid,
          default_uuid,
          defaultImageDimensions,
          selectedImageDimensions,
          sectionId,
          side
        );
      }),
    []
  );

  const saveTextUpdates = registerUndoRedoCreator(Type.updateExemplarText, (payload: UndoRedoAction) => {
    setExemplar(payload as EmergentExemplar, true);
  });

  const handleWordHighlight = (word: ExemplarWord) => {
    dispatch({ type: ActionTypes.SET_HIGHLIGHTED, payload: word });
  };

  const saveHighlightsUpdates = useMemo(
    () =>
      registerUndoRedoCreator(Type.highlight, (payload: UndoRedoAction) => {
        handleWordHighlight(payload as ExemplarWord);
      }),
    []
  );

  return (
    <ExemplarContext.Provider
      value={{
        dispatch: dispatch,
        exemplarState: state,
        setExemplar,
        handleFontStyle,
        updateRelationships: handleUpdateRelationships,
        replaceImage: handleReplaceImage,
        setSavedEdits,
        saveEmergentImageUpdates: saveImageUpdates,
        saveTextUpdates,
        handleWordHighlight,
        saveHighlightsUpdates,
      }}
    >
      {children}
    </ExemplarContext.Provider>
  );
};
